"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Clock, CircleCheck, Loader, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
function SchedulerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const timeSlots = ["09:00 AM - 10:00 AM", "10:00 AM - 11:00 AM", "11:00 AM - 12:00 PM", "01:00 PM - 02:00 PM", "02:00 PM - 03:00 PM", "03:00 PM - 04:00 PM", "04:00 PM - 05:00 PM"];
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-300">
        <div className="mb-6 rounded-full bg-green-100 p-4 dark:bg-green-900/30">
          <CircleCheck className="size-12 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Booking Confirmed!</h3>
        <p className="text-muted-foreground max-w-md mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Schedule Another Demo
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {}
        <div className="lg:col-span-5 space-y-6">
          <div className="rounded-xl border bg-card/50 p-4 shadow-sm">
            <FormField control={control} name="preferred_date" rules={{
            required: "Please select a date"
          }} render={({
            field
          }) => <FormItem className="flex flex-col">
                  <FormLabel className="mb-2 font-semibold">Select Date</FormLabel>
                  <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date)} disabled={date => date < new Date() || date < new Date("1900-01-01")} initialFocus className="rounded-md border shadow-xs bg-background mx-auto" /></SafeHydrate>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="preferred_time" rules={{
          required: "Please select a time slot"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Preferred Time</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a time slot" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {timeSlots.map((slot, i) => <SelectItem key={i} value={slot} data-index={i}>
                        {slot}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormDescription>Choose a one‑hour slot for the demo.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="lg:col-span-7 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField control={control} name="full_name" rules={{
            required: "Full Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField control={control} name="company" rules={{
            required: "Company Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Corp" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
              message: "Invalid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 555-123-4567" {...field} />
                  </FormControl>
                  <FormDescription>Optional – for demo confirmation.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="message" render={({
          field
        }) => <FormItem>
                <FormLabel>Additional Details (Optional)</FormLabel>
                <FormControl>
                  <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30" placeholder="Any specific topics you’d like to cover..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {submitError}
            </div>}

          <div className="pt-4">
            <Button type="submit" className="w-full md:w-auto md:min-w-[200px]" size="lg" disabled={isSubmitting}>
              {isSubmitting ? <>
                  <Loader className="mr-2 size-4 animate-spin" />
                  Scheduling...
                </> : "Confirm Booking"}
            </Button>
          </div>
        </div>
      </div>
    </Form>;
}
export default function DemoScheduler() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="demoscheduler" ref={ref} className="relative w-full overflow-hidden bg-secondary/30 py-24 lg:py-32">
      {}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] size-[600px] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] size-[600px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className={cn("mx-auto max-w-4xl text-center transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          <div className="mb-6 inline-flex items-center justify-center rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <SafeHydrate><CalendarIcon className="mr-2 size-4" /></SafeHydrate>
            Schedule a Live Demo
          </div>
          
          <h2 className="mb-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Experience the Future of <span className="text-primary">Voice AI</span>
          </h2>
          
          <p className="mb-10 text-lg text-muted-foreground md:text-xl">
            Book a personalized session with our product experts. See how MollyCall.ai can automate your inbound and outbound calls, qualify leads, and schedule appointments 24/7.
          </p>

          <Dialog>
            <DialogTrigger asChild>
              <Button size="lg" className="h-14 px-8 text-lg shadow-lg shadow-primary/20 transition-transform hover:scale-105">
                Book Your Demo Now
                <ArrowRight className="ml-2 size-5" />
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl bg-background/95 backdrop-blur-xl border-white/20 dark:border-white/10 shadow-2xl">
              <DialogHeader className="mb-6 border-b pb-4">
                <DialogTitle className="text-2xl font-bold">Schedule Your Demo</DialogTitle>
                <DialogDescription className="text-base">
                  Select a time that works for you. We'll show you how our AI agents can transform your business communication.
                </DialogDescription>
              </DialogHeader>
              
              <FormProvider formId="request_demo_form_home" sectionName="demo-scheduler" defaultValues={{
              full_name: "",
              email: "",
              company: "",
              phone: "",
              preferred_date: undefined,
              preferred_time: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              company: {
                required: true
              },
              preferred_date: {
                required: true
              },
              preferred_time: {
                required: true
              }
            }} submitText="Confirm Booking" successMessage="Your demo request has been received! We've sent a confirmation email with the meeting details.">
                <SchedulerForm />
              </FormProvider>
            </DialogContent>
          </Dialog>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3 text-left">
            {[{
            icon: Clock,
            title: "30-Minute Session",
            desc: "A concise, high-impact walkthrough of our platform capabilities."
          }, {
            icon: CircleCheck,
            title: "Tailored to You",
            desc: "We'll focus on your specific industry challenges and use cases."
          }, {
            icon: Loader,
            title: "Live Interaction",
            desc: "Interact with our AI voice agents in real-time during the call."
          }].map((item, i) => <div key={i} data-index={i} className="group rounded-xl border bg-card p-6 shadow-sm transition-all hover:shadow-md">
                <div className="mb-4 inline-flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <item.icon className="size-5" />
                </div>
                <h3 className="mb-2 font-semibold text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}